// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useRef, useState } from 'react'
import { formatUsTimestamp, formatUsDateLocal } from '../api/format'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, ChevronUp, Loader2, AlertCircle, MessageSquare, Paperclip,
  Send, X, Download, FileText, Image as ImageIcon, File as FileIcon,
} from 'lucide-react'
import * as api from '../api/client'
import { friendlyError } from '../api/errors'
import { useAuth } from '../auth/AuthContext'

const MAX_FILE_BYTES = 10 * 1024 * 1024
const ACCEPT_ATTR =
  'image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/markdown,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
  'application/vnd.openxmlformats-officedocument.presentationml.presentation'

export default function RoadmapDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { me } = useAuth()
  const isAuth = Boolean(me?.authenticated)

  const [data, setData] = useState<api.RoadmapDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [voting, setVoting] = useState(false)

  const [commentText, setCommentText] = useState('')
  const [commentBusy, setCommentBusy] = useState(false)
  const [commentError, setCommentError] = useState<string | null>(null)

  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!id) return
    setLoading(true); setError(null)
    api.getRoadmapDetail(id)
      .then(setData)
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }, [id])

  async function handleVote() {
    if (!data || voting) return
    setVoting(true)
    try {
      const r = await api.submitVote(data.item.id)
      setData({ ...data, item: { ...data.item, vote_count: r.vote_count } })
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setVoting(false)
    }
  }

  function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    if (e.target) e.target.value = '' // allow re-picking same file
    addFiles(files)
  }

  function addFiles(files: File[]) {
    setUploadError(null)
    const accepted: File[] = []
    for (const f of files) {
      if (f.size > MAX_FILE_BYTES) {
        setUploadError(`${f.name} is over 10 MB and was skipped.`)
        continue
      }
      accepted.push(f)
    }
    setPendingFiles(prev => [...prev, ...accepted].slice(0, 5))
    if (files.length + pendingFiles.length > 5) {
      setUploadError('Up to 5 files at a time.')
    }
  }

  function removePending(i: number) {
    setPendingFiles(prev => prev.filter((_, idx) => idx !== i))
  }

  async function postCommentAndUploads() {
    if (!data || commentBusy || uploading) return
    if (!commentText.trim() && pendingFiles.length === 0) {
      setCommentError('Add a comment or attach a file first.')
      return
    }
    setCommentError(null); setUploadError(null)

    let createdCommentId: string | undefined
    if (commentText.trim()) {
      setCommentBusy(true)
      try {
        const c = await api.postRoadmapComment(data.item.id, commentText.trim())
        setData(prev => prev ? { ...prev, comments: [...prev.comments, c] } : prev)
        createdCommentId = c.id
        setCommentText('')
      } catch (e) {
        setCommentError(friendlyError(e))
        setCommentBusy(false)
        return
      }
      setCommentBusy(false)
    }

    if (pendingFiles.length > 0) {
      setUploading(true)
      const uploaded: api.RoadmapAttachment[] = []
      for (const file of pendingFiles) {
        try {
          const a = await api.postRoadmapAttachment(data.item.id, file, createdCommentId)
          uploaded.push(a)
        } catch (e) {
          setUploadError(`${file.name}: ${friendlyError(e)}`)
          break
        }
      }
      if (uploaded.length > 0) {
        setData(prev => prev ? { ...prev, attachments: [...prev.attachments, ...uploaded] } : prev)
      }
      setPendingFiles([])
      setUploading(false)
    }
  }

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={loadingBox}><Loader2 size={20} className="spin" /> Loading…</div>
      </div>
    )
  }
  if (error) {
    return (
      <div style={pageStyle}>
        <button onClick={() => navigate('/roadmap')} style={backBtn}><ArrowLeft size={14} /> Roadmap</button>
        <div style={errorBox}><AlertCircle size={16} /> {error}</div>
      </div>
    )
  }
  if (!data) return null

  const { item, comments, attachments } = data
  const itemAttachments = attachments.filter(a => a.comment_id == null)

  return (
    <div style={pageStyle}>
      <button onClick={() => navigate('/roadmap')} style={backBtn}>
        <ArrowLeft size={14} /> Roadmap
      </button>

      {/* Header */}
      <header style={headerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={pillRowStyle}>
              <Pill label={item.status} tone="status" value={item.status} />
              {item.sprint && <Pill label={`Sprint: ${item.sprint}`} />}
              {item.epic && <Pill label={`Epic: ${item.epic}`} />}
              <Pill label={item.category} />
            </div>
            <h1 style={titleStyle}>{item.title}</h1>
            {item.summary && <p style={summaryStyle}>{item.summary}</p>}
          </div>
          <button onClick={handleVote} disabled={voting} style={voteBtnLg} aria-label="Upvote">
            <ChevronUp size={18} />
            <strong>{item.vote_count}</strong>
          </button>
        </div>

        {/* Meta line */}
        <div style={metaLineStyle}>
          {item.owning_persona && <span>Owner: <strong>{item.owning_persona}</strong></span>}
          {item.effort_estimate && <span>Effort: <strong>{item.effort_estimate}</strong></span>}
          {item.shipped_at && <span>Shipped: <strong>{formatDate(item.shipped_at)}</strong></span>}
          {item.shipped_in_pr && <span>PR: <strong>{item.shipped_in_pr}</strong></span>}
        </div>
      </header>

      {/* Description */}
      {item.description && (
        <section style={sectionStyle}>
          <h2 style={sectionTitle}>Description</h2>
          <div style={descriptionStyle}>{item.description}</div>
        </section>
      )}

      {/* Item-level attachments */}
      {itemAttachments.length > 0 && (
        <section style={sectionStyle}>
          <h2 style={sectionTitle}>Attachments</h2>
          <ul style={attListStyle}>
            {itemAttachments.map(a => <AttachmentRow key={a.id} a={a} />)}
          </ul>
        </section>
      )}

      {/* Comments */}
      <section style={sectionStyle}>
        <h2 style={sectionTitle}>
          <MessageSquare size={14} aria-hidden="true" style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
          Discussion ({comments.length})
        </h2>

        {comments.length === 0 ? (
          <div style={emptyBox}>No comments yet. Be the first to weigh in.</div>
        ) : (
          <ul style={commentListStyle}>
            {comments.map(c => {
              const cAtt = attachments.filter(a => a.comment_id === c.id)
              return (
                <li key={c.id} style={commentItemStyle}>
                  <div style={commentHeaderStyle}>
                    <strong>{c.author_name}</strong>
                    <span style={commentRoleStyle}>{c.author_role}</span>
                    <span style={{ flex: 1 }} />
                    <span style={commentTimeStyle}>{formatTimestamp(c.created_at)}</span>
                  </div>
                  <div style={commentBodyStyle}>{c.body}</div>
                  {cAtt.length > 0 && (
                    <ul style={{ ...attListStyle, marginTop: 8 }}>
                      {cAtt.map(a => <AttachmentRow key={a.id} a={a} compact />)}
                    </ul>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        {/* Composer */}
        {isAuth ? (
          <div style={composerStyle}>
            <textarea
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder="Share your thoughts, questions, or requests…"
              maxLength={4000}
              rows={3}
              style={textareaStyle}
              disabled={commentBusy || uploading}
            />

            {/* Pending attachments */}
            {pendingFiles.length > 0 && (
              <ul style={pendingListStyle}>
                {pendingFiles.map((f, i) => (
                  <li key={i} style={pendingItemStyle}>
                    <FileIcon size={13} aria-hidden="true" />
                    <span style={{ flex: 1 }}>{f.name}</span>
                    <span style={{ color: 'var(--color-text-mute)', fontSize: 11 }}>{formatBytes(f.size)}</span>
                    <button onClick={() => removePending(i)} style={pendingRemoveStyle} aria-label="Remove">
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {commentError && <div style={errorInlineStyle}><AlertCircle size={14} /> {commentError}</div>}
            {uploadError && <div style={errorInlineStyle}><AlertCircle size={14} /> {uploadError}</div>}

            <div style={composerActionsStyle}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPT_ATTR}
                onChange={onPickFiles}
                style={{ display: 'none' }}
              />
              <button onClick={() => fileInputRef.current?.click()} style={attachBtn} disabled={uploading}>
                <Paperclip size={14} /> Attach
              </button>
              <span style={{ flex: 1, fontSize: 11, color: 'var(--color-text-mute)' }}>
                Up to 5 files, 10 MB each. Images, PDF, Office docs.
              </span>
              <button
                onClick={postCommentAndUploads}
                disabled={commentBusy || uploading || (!commentText.trim() && pendingFiles.length === 0)}
                style={postBtn}
              >
                {commentBusy || uploading
                  ? <><Loader2 size={14} className="spin" /> Posting…</>
                  : <><Send size={14} /> Post</>}
              </button>
            </div>
          </div>
        ) : (
          <div style={authNudgeStyle}>
            <Link to="/login" style={{ color: 'var(--color-primary)', fontWeight: 500 }}>Sign in</Link>{' '}
            to comment or attach files.
          </div>
        )}
      </section>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Pill({ label, tone, value }: { label: string; tone?: 'status'; value?: string }) {
  let bg = '#f1f5f9', fg = '#334155'
  if (tone === 'status' && value) {
    if (value === 'in_progress') { bg = '#fef3c7'; fg = '#854d0e' }
    else if (value === 'planned') { bg = '#dbeafe'; fg = '#1e40af' }
    else if (value === 'done' || value === 'shipped') { bg = '#dcfce7'; fg = '#166534' }
    else if (value === 'wont_fix') { bg = '#fee2e2'; fg = '#991b1b' }
  }
  return <span style={{ ...pillStyle, background: bg, color: fg }}>{label}</span>
}

function AttachmentRow({ a, compact = false }: { a: api.RoadmapAttachment; compact?: boolean }) {
  const isImage = a.content_type.startsWith('image/')
  const isPdf = a.content_type === 'application/pdf'
  const Icon = isImage ? ImageIcon : isPdf ? FileText : FileIcon

  return (
    <li style={attItemStyle}>
      <Icon size={compact ? 13 : 15} aria-hidden="true" style={{ flexShrink: 0 }} />
      <a href={a.download_url} target="_blank" rel="noreferrer" style={attLinkStyle}>
        {a.file_name}
      </a>
      <span style={attMetaStyle}>{formatBytes(a.size_bytes)}</span>
      <span style={attMetaStyle}>· {a.uploader_name}</span>
      <a href={a.download_url} target="_blank" rel="noreferrer" style={attDownloadStyle} aria-label="Download">
        <Download size={13} />
      </a>
    </li>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(s: string) {
  return formatUsTimestamp(s)
}
function formatDate(s: string) {
  return formatUsDateLocal(s)
}
function formatBytes(b: number) {
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / 1024 / 1024).toFixed(1)} MB`
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const pageStyle: React.CSSProperties = { maxWidth: '100%', margin: '0 auto', padding: '24px 24px 48px' }
const backBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
  background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 6,
  fontSize: 12, color: 'var(--color-text-soft)', cursor: 'pointer', fontFamily: 'inherit',
  marginBottom: 16,
}
const headerStyle: React.CSSProperties = { marginBottom: 24 }
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 28, fontWeight: 700, margin: '8px 0 6px', letterSpacing: -0.5, lineHeight: 1.25,
}
const summaryStyle: React.CSSProperties = { color: 'var(--color-text-soft)', fontSize: 15, margin: 0, lineHeight: 1.5 }
const pillRowStyle: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 6 }
const pillStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
  padding: '2px 8px', borderRadius: 10, textTransform: 'capitalize',
}
const metaLineStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12, fontSize: 12,
  color: 'var(--color-text-soft)',
}
const voteBtnLg: React.CSSProperties = {
  display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  minWidth: 60, padding: '10px 14px',
  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
  borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
  color: 'var(--color-text)', fontSize: 14,
}

const sectionStyle: React.CSSProperties = { marginBottom: 28 }
const sectionTitle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
  color: 'var(--color-text-mute)', margin: '0 0 10px',
}
const descriptionStyle: React.CSSProperties = {
  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
  borderRadius: 8, padding: 16, fontSize: 14, lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
}

const commentListStyle: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0 }
const commentItemStyle: React.CSSProperties = {
  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
  borderRadius: 8, padding: 14, marginBottom: 10,
}
const commentHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 13,
}
const commentRoleStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
  color: 'var(--color-text-mute)', background: 'var(--color-surface-alt)',
  padding: '1px 6px', borderRadius: 3,
}
const commentTimeStyle: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-mute)' }
const commentBodyStyle: React.CSSProperties = {
  fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap',
  color: 'var(--color-text)',
}

const composerStyle: React.CSSProperties = {
  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
  borderRadius: 8, padding: 14, marginTop: 14,
}
const textareaStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: 70,
  padding: 10, fontSize: 14, fontFamily: 'inherit',
  border: '1px solid var(--color-border)', borderRadius: 6,
  background: 'var(--color-surface)', color: 'var(--color-text)',
}
const composerActionsStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap',
}
const attachBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px',
  background: 'transparent', border: '1px solid var(--color-border)',
  borderRadius: 6, fontSize: 12, color: 'var(--color-text-soft)',
  cursor: 'pointer', fontFamily: 'inherit',
}
const postBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  background: 'var(--color-primary, #0d9488)', color: '#fff',
  border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500,
  cursor: 'pointer', fontFamily: 'inherit',
}

const pendingListStyle: React.CSSProperties = { listStyle: 'none', margin: '10px 0 0', padding: 0 }
const pendingItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
  background: 'var(--color-surface-alt)', border: '1px solid var(--color-border)',
  borderRadius: 6, marginBottom: 4, fontSize: 12,
}
const pendingRemoveStyle: React.CSSProperties = {
  background: 'transparent', border: 'none', color: 'var(--color-text-mute)',
  cursor: 'pointer', padding: 2, display: 'inline-flex', alignItems: 'center',
}

const attListStyle: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0 }
const attItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
  borderRadius: 6, marginBottom: 4, fontSize: 13,
}
const attLinkStyle: React.CSSProperties = {
  color: 'var(--color-primary, #0d9488)', textDecoration: 'none', fontWeight: 500,
  flexShrink: 0,
}
const attMetaStyle: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-mute)' }
const attDownloadStyle: React.CSSProperties = {
  marginLeft: 'auto', color: 'var(--color-text-mute)', display: 'inline-flex',
}

const authNudgeStyle: React.CSSProperties = {
  marginTop: 14, padding: 14,
  background: 'var(--color-surface-alt)', border: '1px dashed var(--color-border)',
  borderRadius: 8, fontSize: 13, color: 'var(--color-text-soft)',
}

const loadingBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: 24,
  color: 'var(--color-text-soft)', fontSize: 14,
}
const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: 14,
  background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13,
}
const errorInlineStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  marginTop: 8, padding: '6px 10px',
  background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 12,
}
const emptyBox: React.CSSProperties = {
  padding: 14, fontSize: 13, color: 'var(--color-text-mute)',
  background: 'var(--color-surface)', border: '1px dashed var(--color-border)',
  borderRadius: 8,
}
